import { useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { parseUtcDate } from '../../lib/dates'
import { usuariosService } from '../../api/services'
import { usePagedRequest, useRequest } from '../../hooks'
import type { UsuarioResponse, RoleResponse } from '../../api/models/response'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { useT } from '../../i18n'

const excelColumns: ExcelColumn<UsuarioResponse>[] = [
  { header: 'Nombre', accessor: (u) => u.fullName },
  { header: 'Username', accessor: (u) => u.username },
  { header: 'Documento', accessor: (u) => `${u.documentType === 1 ? 'DNI' : 'CE'}: ${u.identityDoc}` },
  { header: 'Roles', accessor: (u) => u.roles.map((r) => r.roleName).join(', ') },
  { header: 'Estado', accessor: (u) => u.userStatusDescription },
]

export default function UsuariosPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()

  const [search, setSearch] = useState('')
  const [rolFilter, setRolFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const { items: rawUsuarios, isFetching: loading, error, refresh, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    usuariosService.getAll,
    { params: { search: search.trim() || undefined } },
  )

  // Client-side filters
  const usuarios = rawUsuarios.filter((u) => {
    if (rolFilter && !u.roles.some((r) => r.roleId === rolFilter)) return false
    if (statusFilter === 'active' && u.userStatus !== 2) return false
    if (statusFilter === 'inactive' && u.userStatus === 2) return false
    return true
  })

  const { data: roles = [] } = useRequest<RoleResponse[]>(usuariosService.getRoles, { onInit: true, showError: false })

  const getInitials = (u: UsuarioResponse) => {
    const first = u.names?.[0] ?? ''
    const last = u.paternalSurname?.[0] ?? ''
    return `${first}${last}`.toUpperCase()
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return t('usuarios.never')
    return parseUtcDate(iso).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const isActive = (u: UsuarioResponse) => u.userStatus === 2

  const handleToggleStatus = async (usuario: UsuarioResponse) => {
    const newStatus = isActive(usuario) ? 3 : 2

    await usuariosService.update(usuario.id, {
      names: usuario.names,
      paternalSurname: usuario.paternalSurname,
      maternalSurname: usuario.maternalSurname ?? undefined,
      documentType: usuario.documentType,
      identityDoc: usuario.identityDoc,
      email: usuario.email ?? undefined,
      phoneMobile: usuario.phoneMobile ?? undefined,
      userStatus: newStatus,
      roleIds: usuario.roles.map((r) => r.roleId),
    })

    refresh()
  }

  const handleDelete = async (usuario: UsuarioResponse) => {
    await usuariosService.delete(usuario.id)
    refresh()
  }

  const getRolColor = (code: string): string => {
    const colorMap: Record<string, string> = {
      superadmin: '#c8442f',
      admin: '#d49a2a',
      operador: '#3d6fa8',
      transportista: '#8B5CF6',
      proveedor: '#2a8a87',
    }
    return colorMap[code] ?? brand.green
  }

  const selectStyles: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    background: colors.bgHover,
    color: colors.text,
    outline: 'none',
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title={t('usuarios.title')}
        subtitle={t('usuarios.subtitle')}
        colors={colors}
      />

      <DataList<UsuarioResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('usuarios.searchPlaceholder')}
        onExport={() => exportToExcel(usuarios, excelColumns, 'usuarios')}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={usuarios.length}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="usuarios"
        brandColor={brand.green}
        toolbarExtra={
          <>
            <select
              value={rolFilter}
              onChange={(e) => setRolFilter(e.target.value)}
              style={selectStyles}
            >
              <option value="">{t('usuarios.allRoles')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              style={selectStyles}
            >
              <option value="all">{t('usuarios.allStatuses')}</option>
              <option value="active">{t('usuarios.activeFilter')}</option>
              <option value="inactive">{t('usuarios.inactiveFilter')}</option>
            </select>
          </>
        }
        headers={[
          t('usuarios.headers.usuario'),
          t('usuarios.headers.username'),
          t('usuarios.headers.documento'),
          t('usuarios.headers.roles'),
          t('usuarios.headers.estado'),
        ]}
        data={usuarios}
        loading={loading}
        error={error}
        emptyMessage={t('usuarios.emptyMessage')}
        loadingMessage={t('usuarios.loadingMessage')}
        colors={colors}
        keyExtractor={(u) => u.id}
        renderRow={(usuario) => (
          <>
            {/* Avatar + Name */}
            <DataTable.Cell>
              <Flex alignItems="center" gap={3}>
                <Box
                  width="36px"
                  height="36px"
                  borderRadius="full"
                  bg={`linear-gradient(135deg, ${brand.green} 0%, ${brand.gold} 100%)`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontSize="xs" fontWeight="bold" color="white">
                    {getInitials(usuario)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={colors.text} whiteSpace="nowrap">
                    {usuario.fullName}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {t('usuarios.lastAccess')}{formatDate(usuario.lastLoginAt)}
                  </Text>
                </Box>
              </Flex>
            </DataTable.Cell>

            {/* Username */}
            <DataTable.Cell>
              <Text fontSize="sm" color={colors.textSecondary}>
                {usuario.username}
              </Text>
            </DataTable.Cell>

            {/* Documento */}
            <DataTable.Cell>
              <Text fontFamily="monospace" fontSize="sm" color={colors.text}>
                {usuario.documentType === 1 ? 'DNI' : 'CE'}: {usuario.identityDoc}
              </Text>
            </DataTable.Cell>

            {/* Roles */}
            <DataTable.Cell>
              <Flex gap={1} flexWrap="wrap">
                {usuario.roles.map((rol) => (
                  <Box
                    key={rol.roleId}
                    display="inline-block"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="10px"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    bg={`${getRolColor(rol.roleCode)}20`}
                    color={getRolColor(rol.roleCode)}
                    whiteSpace="nowrap"
                  >
                    {rol.roleName}
                  </Box>
                ))}
              </Flex>
            </DataTable.Cell>

            {/* Status */}
            <DataTable.Cell>
              <Box
                display="inline-block"
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="10px"
                fontWeight="bold"
                textTransform="uppercase"
                bg={isActive(usuario) ? 'rgba(42, 138, 135, 0.2)' : 'rgba(200, 68, 47, 0.2)'}
                color={isActive(usuario) ? '#2a8a87' : '#c8442f'}
              >
                {usuario.userStatusDescription}
              </Box>
            </DataTable.Cell>
          </>
        )}
        actions={(usuario) => [
          { label: t('usuarios.actions.edit'), onClick: () => navigate(`/app/usuarios/${usuario.id}`) },
          {
            label: isActive(usuario) ? t('usuarios.actions.deactivate') : t('usuarios.actions.activate'),
            onClick: () => handleToggleStatus(usuario),
            color: isActive(usuario) ? '#d49a2a' : '#2a8a87',
          },
          { label: t('usuarios.actions.delete'), onClick: () => handleDelete(usuario), color: '#c8442f' },
        ]}
      />
    </VStack>
  )
}
